import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { useVerifyDomain } from '#api/domains.queries.ts';
import { handleServerFormError, ValidationError, wasReworded } from '#api/errors.ts';
import { adminOIDCProvidersQueryOptions } from '#api/oidc-providers.queries.ts';
import { CheckCircle } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { useZodParams } from '#hooks/useZodParams.ts';
import { useAdminEnableBannerProviderId, useShowEnableBannerForProvider } from '../list/useShowEnableBanner.ts';
import { useGetProvidersForDomain } from './useGetProvidersForDomain.ts';
import { VerificationSteps } from './VerificationSteps.tsx';

const PARAM_SCHEMA = { tab: 'scalar' } as const;

export function DomainVerificationPage() {
  const { domainId } = useZodParams({ domainId: z.string() });
  // The admin provider form links here with ?tab=admin so we can send the user back to the right tab
  const [params] = useUrlParams(PARAM_SCHEMA);
  const returnTab = params.tab === 'admin' ? 'admin' : 'endpoint';
  const verifyDomainMutation = useVerifyDomain();
  const getProvidersForDomain = useGetProvidersForDomain();
  const { setProviderId: setProviderIdToPromptForEnable } = useShowEnableBannerForProvider();
  const [, setAdminProviderIdToPromptForEnable] = useAdminEnableBannerProviderId();
  const queryClient = useQueryClient();
  const [errorMsg, setErrMsg] = useState<string | null>(null);
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false);

  async function handleVerify() {
    setErrMsg(null);
    try {
      await verifyDomainMutation.mutateAsync(domainId);
      const providers = await getProvidersForDomain(domainId);
      if (providers.length === 1 && providers[0] !== undefined) {
        setProviderIdToPromptForEnable(providers[0].id);
      }
      const { data: adminProviders } = await queryClient
        .fetchQuery(adminOIDCProvidersQueryOptions)
        .catch(() => ({ data: [] }));
      const adminProvider = adminProviders.find((p) => p.domainID === domainId && !p.enabled);
      if (adminProvider) {
        setAdminProviderIdToPromptForEnable(adminProvider.id);
      }
      setIsSuccessful(true);
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, (err) => {
          if (wasReworded(err)) {
            setErrMsg(err.message);
            return true;
          }
          setErrMsg('Could not validate domain.  Please try again or contact Defined Networking support');
          return true;
        });
      }
    }
  }

  return (
    <MainLayout header="Verify domain">
      <VerificationSteps domainId={domainId} />
      {errorMsg ?
        <Banner variant="alert">{errorMsg}</Banner>
      : null}
      {isSuccessful ?
        <Banner variant="success" Icon={CheckCircle}>
          We found the DNS record. Your domain is now verified.
        </Banner>
      : null}
      <ConfirmChangesBanner
        title=""
        isOpen={true}
        primaryButton={
          isSuccessful ?
            <LinkButton variant="primary" to={`/settings/sso?tab=${returnTab}`}>
              View providers
            </LinkButton>
          : <Button variant="primary" type="button" onClick={handleVerify}>
              Verify
            </Button>
        }
        secondaryButton={
          isSuccessful ? null : (
            <LinkButton variant="link" to={`/settings/sso?tab=${returnTab}`}>
              Verify later
            </LinkButton>
          )
        }
      />
    </MainLayout>
  );
}
